import React from 'react';
import { CustomerModel, CustomerOrder } from '@app/interfaces/customer';
import { Table, Input, Space, Button, message, Modal, Select, Form, Alert } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { formatDateTime, formatMoney } from '@app/utils/utils';
import { DayjsDatePicker } from '@app/components/common/pickers/DayjsDatePicker';
import dayjs from 'dayjs';
import NumberFormatRender from '@app/components/common/NumberFormatRender';
import { Link } from 'react-router-dom';
import OrderTypeRender from '@app/components/order/components/OrderTypeRender';
import OrderStatus from '@app/components/order/components/OrderStatus';
import { TablePaginationConfig } from 'antd/es/table';
import { CalculatorTwoTone, PrinterOutlined, DownloadOutlined } from '@ant-design/icons';
import { MdPayments } from 'react-icons/md';
import { VscCombine } from 'react-icons/vsc';
import PaymentOrderForm from '@app/components/partner/customer/PaymentOrderForm';
import { createLedger } from '@app/store/slices/ledgerSlice';
import { useAppDispatch } from '@app/hooks/reduxHooks';
import { getCustomer, recalculateCustomerDebit } from '@app/store/slices/customerSlice';
import ReactDOMServer from 'react-dom/server';
import PrintOrder from '@app/components/order/detail/PrintOrder';
import { combineOrderApi, printCombineOrderApi } from '@app/api/order.api';
import { getBanksApi, getBankShopQRCode } from '@app/api/bank.api';
import { useAppSetting } from '@app/contexts/AppSettingContext';
import { dayjsPresetRanges } from '@app/components/common/pickers/datePresets';
import { showTotalRecords } from '@app/utils/tablePagination';
import { printHtmlBody } from '@app/utils/printDocument';
import { orderQrUrl as buildOrderQrUrl, toQrDataUrl } from '@app/utils/orderQr';
import { DATE_FORMAT } from '@app/constants/dateFormats';

interface CustomerInfoProps {
  record: CustomerModel;
}

const DebitCustomer: React.FC<CustomerInfoProps> = ({ record }) => {
  const { logoUrl, storeName, storeAddress, storePhone, storeWebsite } = useAppSetting();
  const data = record?.orders || [];

  // Filter state
  const [orderCode, setOrderCode] = React.useState('');
  const [dateRange, setDateRange] = React.useState<[string | null, string | null]>([null, null]);
  const [selected, setSelected] = React.useState<CustomerOrder[]>([]);
  const [showPaymentForm, setShowPaymentForm] = React.useState(false);
  const [recalculating, setRecalculating] = React.useState(false);
  const [showCombineModal, setShowCombineModal] = React.useState(false);
  const [combineForm] = Form.useForm();
  const [combiningOrders, setCombiningOrders] = React.useState(false);
  const [combinedOrderId, setCombinedOrderId] = React.useState<number | null>(null);
  const [printingCombined, setPrintingCombined] = React.useState(false);
  const [bank, setBank] = React.useState<any>(null);

  const dispatch = useAppDispatch();

  // Fetch bank info for QR code on mount
  React.useEffect(() => {
    const fetchBankInfo = async () => {
      try {
        const newParams = { bank_type_eq: 0, active_true: true }; // type is => shop
        const response = await getBanksApi(newParams);
        const bankData = response?.rows.map((item: any) => item.attributes)[0] || null;
        setBank(bankData);
      } catch (error) {
        console.error('Error fetching bank info:', error);
      }
    };
    fetchBankInfo();
  }, []);

  // Pagination state
  const [pagination, setPagination] = React.useState<TablePaginationConfig>({
    current: 1,
    pageSize: 10,
    total: data.length,
    showSizeChanger: true,
    showTotal: showTotalRecords,
    position: ['bottomRight'],
    pageSizeOptions: ['10', '20', '50', '100'],
  });

  // Filtered data
  const filteredData = React.useMemo(() => {
    return data.filter((order) => {
      // Filter by order code
      const matchOrderCode = orderCode ? order.order_code?.toLowerCase().includes(orderCode.toLowerCase()) : true;

      // Filter by date range
      let matchDate = true;
      if (dateRange[0] && dateRange[1]) {
        const orderDate = new Date(order.trans_date);
        const startDate = new Date(dateRange[0]);
        const endDate = new Date(dateRange[1]);
        matchDate = orderDate >= startDate && orderDate <= endDate;
      }
      return matchOrderCode && matchDate;
    });
  }, [data, orderCode, dateRange]);

  // Update pagination total when filteredData changes
  React.useEffect(() => {
    setPagination((prev) => ({
      ...prev,
      total: filteredData.length,
      current: 1, // Reset to first page on filter change
    }));
  }, [filteredData.length]);

  // Paginated data
  const paginatedData = React.useMemo(() => {
    const start = ((pagination.current || 1) - 1) * (pagination.pageSize || 10);
    const end = start + (pagination.pageSize || 10);
    return filteredData.slice(start, end);
  }, [filteredData, pagination.current, pagination.pageSize]);

  const columns: ColumnsType<CustomerOrder> = [
    {
      title: 'Mã hoá đơn',
      dataIndex: 'order_code',
      key: 'order_code',
      render: (value: any, record: any) => {
        if (record.order_type === 'return') {
          return (
            <Link to={`/order-returns/${record.order_id}`} target="_blank">
              {value}
            </Link>
          );
        }
        return (
          <Link to={`/orders/${record.order_id}`} target="_blank">
            {value}
          </Link>
        );
      },
    },
    {
      title: 'Thời gian',
      dataIndex: 'trans_date',
      key: 'trans_date',
      render: (value: any) => formatDateTime(value, 'date'),
    },
    {
      title: 'Loại',
      dataIndex: 'order_type',
      key: 'order_order_type',
      render: (value: any) => <OrderTypeRender status={value} />,
    },
    {
      title: 'Giá trị',
      dataIndex: 'amount',
      key: 'amount',
      render: (value: any) => <NumberFormatRender value={value} />,
    },
    {
      title: 'Công nợ',
      dataIndex: 'debit_amount',
      key: 'debit_amount',
      render: (value: any) => <NumberFormatRender value={value} />,
    },
    {
      title: 'Trạng thái',
      key: 'status',
      dataIndex: 'status',
      render: (value: any) => <OrderStatus status={value} />,
    },
    {
      title: 'Người bán',
      dataIndex: 'seller',
      key: 'seller',
    },
    {
      title: 'Kênh bán',
      key: 'channel',
      dataIndex: 'channel',
    },
  ];

  const rowSelection = {
    onChange: (selectedRowKeys: React.Key[], selectedRows: CustomerOrder[]) => {
      setSelected(selectedRows);
    },
  };

  const handleTableChange = (newPagination: TablePaginationConfig) => {
    setPagination((prev) => ({
      ...prev,
      current: newPagination.current,
      pageSize: newPagination.pageSize,
    }));
  };

  const handleExport = () => {
    const headers = ['Mã hoá đơn', 'Thời gian', 'Loại', 'Giá trị', 'Công nợ', 'Trạng thái', 'Người bán', 'Kênh bán'];
    const rows = filteredData.map((order) => [
      order.order_code,
      formatDateTime(order.trans_date, 'date'),
      order.order_type === 'order' ? 'Bán hàng' : 'Trả hàng',
      order.amount,
      order.debit_amount,
      order.status,
      order.seller,
      order.channel,
    ]);
    const csvContent = [headers, ...rows].map((row) => row.map((val) => `"${val ?? ''}"`).join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'công-nợ-khách-hàng.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleRecalculate = async () => {
    setRecalculating(true);
    try {
      await dispatch(recalculateCustomerDebit(Number(record.id))).unwrap();
      await dispatch(getCustomer(Number(record.id)));
      message.success('Đã tính lại công nợ thành công');
    } catch (error: any) {
      message.error(error?.message || 'Có lỗi xảy ra khi tính lại công nợ');
    } finally {
      setRecalculating(false);
    }
  };

  const handlePayment = () => {
    setShowPaymentForm(true);
  };

  const onCreatePayment = async (values: any) => {
    try {
      await Promise.all(
        selected.map((order) => {
          const payload = {
            ...values,
            name: `Thanh toán đơn hàng - ${order.order_code}`,
            payee_recipient_type: 'customer',
            payee_recipient_name: record?.id,
            transaction_type: 'receivable',
            amount: Math.abs(order.debit_amount),
            posted: true,
            order_id: order.order_id,
            store_id: record.store_id,
          };
          return dispatch(createLedger(payload)).unwrap();
        }),
      );

      setShowPaymentForm(false);
      // Optionally: refresh data here if needed
      await dispatch(getCustomer(Number(record.id)));
    } catch (error: any) {
      message.error(error?.message || 'Có lỗi xảy ra khi thanh toán');
    }
  };

  const absoluteLogoUrl =
    logoUrl.startsWith('http') || logoUrl.startsWith('data:')
      ? logoUrl
      : `${window.location.origin}${logoUrl.startsWith('/') ? '' : '/'}${logoUrl}`;

  const fetchImageAsBase64 = async (url: string): Promise<string> => {
    if (!url || url.startsWith('data:')) return url;
    try {
      const res = await fetch(url);
      const blob = await res.blob();
      return await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch {
      return url;
    }
  };

  const printContent = (orderData: any, qrCodeUrl?: string, logoSrc = absoluteLogoUrl, orderQrDataUrl?: string) => {
    return ReactDOMServer.renderToString(
      <PrintOrder
        record={orderData}
        qrCodeUrl={qrCodeUrl}
        logoUrl={logoSrc}
        storeName={storeName}
        storeAddress={storeAddress}
        storePhone={storePhone}
        storeWebsite={storeWebsite}
        orderQrUrl={orderQrDataUrl}
      />,
    );
  };

  const openPrintWindow = async (content: string) => {
    try {
      await printHtmlBody(content, { title: 'In đơn hàng' });
    } catch (error) {
      message.error('Có lỗi xảy ra khi in.');
    }
  };

  const handleCombineSelected = () => {
    if (selected.length < 2) {
      message.warning('Vui lòng chọn ít nhất 2 đơn hàng để gộp');
      return;
    }
    setShowCombineModal(true);
  };

  const onCombineSubmit = async () => {
    try {
      const values = await combineForm.validateFields();
      const mainOrderCode = values.main_order_code;

      setCombiningOrders(true);

      // Find the main order
      const mainOrder = selected.find((order) => order.order_code === mainOrderCode);
      if (!mainOrder) {
        message.error('Không tìm thấy đơn hàng chính');
        return;
      }

      // Get all order codes to combine
      const allOrderCodes = selected.map((order) => order.order_code);

      // Combine the main order with all order codes
      await combineOrderApi(Number(mainOrder.order_id), { group_code: allOrderCodes });

      setCombinedOrderId(mainOrder.order_id);

      message.success('Gộp đơn hàng thành công');
      setShowCombineModal(false);
      combineForm.resetFields();
      setSelected([]);
      await dispatch(getCustomer(Number(record.id)));
    } catch (error: any) {
      message.error(error?.message || 'Có lỗi xảy ra khi gộp đơn hàng');
    } finally {
      setCombiningOrders(false);
    }
  };

  const handlePrintCombinedBill = async () => {
    if (!combinedOrderId) {
      message.warning('Không tìm thấy đơn hàng gộp để in');
      return;
    }

    try {
      setPrintingCombined(true);
      const response = await printCombineOrderApi(combinedOrderId);
      const combinedOrder = response?.data?.attributes || response || null;

      if (combinedOrder && Object.keys(combinedOrder).length > 0) {
        let qrCodeUrl = '';
        if (bank && combinedOrder.total_amount) {
          try {
            qrCodeUrl = (await getBankShopQRCode(bank, combinedOrder.total_amount, `Chuyen khoan`)) ?? '';
          } catch {
            // QR code optional
          }
        }

        const logoBase64 = await fetchImageAsBase64(absoluteLogoUrl);
        // QR mở trang xử lý nhanh của đơn (shipper quét để cập nhật giao hàng).
        const code = Array.isArray(combinedOrder.order_code) ? combinedOrder.order_code[0] : combinedOrder.order_code;
        const orderQr = code ? await toQrDataUrl(buildOrderQrUrl(code), 300).catch(() => undefined) : undefined;
        openPrintWindow(printContent(combinedOrder, qrCodeUrl, logoBase64, orderQr));
      } else {
        message.error('Không thể lấy dữ liệu hóa đơn gộp');
      }
    } catch (error: any) {
      message.error(error?.message || 'Có lỗi xảy ra khi in hóa đơn gộp');
    } finally {
      setPrintingCombined(false);
    }
  };

  const PaymentButton = () => {
    return (
      <>
        <span className="me-2">
          Đã chọn <strong>{selected.length}</strong> đơn hàng
        </span>
        <Space>
          <Button type="primary" title="Thanh toán" onClick={handlePayment}>
            <MdPayments className="me-2" /> Thanh toán
          </Button>
          <Button type="default" title="Gộp đơn" onClick={handleCombineSelected}>
            <VscCombine className="me-2" /> Gộp đơn
          </Button>
        </Space>
      </>
    );
  };

  // Ant Design DatePicker
  const { RangePicker } = DayjsDatePicker;

  const sumFromOrders = data.reduce((acc, o) => acc + (o.debit_amount ?? 0), 0);
  const storedDebit = record?.debit_amount ?? 0;
  const diff = storedDebit - sumFromOrders;

  return (
    <div className="px-3">
      <Space align="end" className="px-2 flex-between">
        <Space size={24}>
          <span>
            Nợ đã ghi nhận: <strong className="text-danger">{formatMoney(storedDebit)}</strong>
          </span>
          |
          <span>
            Từ đơn hàng: <strong className="text-primary">{formatMoney(sumFromOrders)}</strong>
          </span>
          {diff !== 0 && (
            <>
              |
              <span>
                Chênh lệch:{' '}
                <strong className={diff < 0 ? 'text-danger' : 'text-success'}>
                  {formatMoney(diff)}
                </strong>
              </span>
            </>
          )}
        </Space>

        <Space size={8}>
          <Button
            icon={<CalculatorTwoTone />}
            onClick={handleRecalculate}
            loading={recalculating}
            size="small"
            type="primary"
            danger
          >
            Tính lại công nợ
          </Button>
          <Input
            placeholder="Tìm mã hoá đơn"
            value={orderCode}
            onChange={(e) => setOrderCode(e.target.value)}
            allowClear
            size="small"
            style={{ width: 180, height: 36 }}
          />
          <RangePicker
            format={DATE_FORMAT}
            size="small"
            value={dateRange[0] && dateRange[1] ? [dayjs(dateRange[0]), dayjs(dateRange[1])] : [null, null]}
            onChange={(_: any, dateStrings: string[]) => setDateRange([dateStrings[0] || null, dateStrings[1] || null])}
            style={{ width: 220 }}
            ranges={dayjsPresetRanges()}
          />
          <Button size="small" type="primary" title="Xuất excel" onClick={handleExport} icon={<DownloadOutlined />}>
            Export
          </Button>
        </Space>
      </Space>

      <div className="mb-2">{selected.length > 0 ? <PaymentButton /> : 'Chưa chọn đơn hàng'}</div>

      {combinedOrderId && (
        <div className="mb-3">
          <Button
            type="primary"
            onClick={handlePrintCombinedBill}
            loading={printingCombined}
            icon={<PrinterOutlined />}
          >
            In hóa đơn gộp (ID: {combinedOrderId})
          </Button>
        </div>
      )}
      {!combinedOrderId && (
        <div className="mb-3" style={{ color: '#999', fontSize: '12px' }}>
          💡 Sau khi gộp đơn hàng, nút "In hóa đơn gộp" sẽ xuất hiện
        </div>
      )}

      <Table
        scroll={{ x: 'max-content' }}
        rowKey="order_id"
        columns={columns}
        dataSource={paginatedData}
        size="small"
        rowSelection={rowSelection}
        pagination={pagination}
        onChange={handleTableChange}
      />

      {showPaymentForm && (
        <PaymentOrderForm
          open={showPaymentForm}
          onCreate={onCreatePayment}
          onCancel={() => {
            setShowPaymentForm(false);
          }}
          title={`Thanh toán ${selected.length} đơn hàng`}
          customer={record}
          orders={selected}
        />
      )}

      <Modal
        open={showCombineModal}
        title="Gộp đơn hàng"
        onCancel={() => {
          setShowCombineModal(false);
          combineForm.resetFields();
        }}
        closable={true}
        footer={[
          <Button
            key="cancel"
            onClick={() => {
              setShowCombineModal(false);
              combineForm.resetFields();
            }}
          >
            Huỷ
          </Button>,
          <Button key="submit" type="primary" loading={combiningOrders} onClick={onCombineSubmit} icon={<VscCombine />}>
            Gộp đơn
          </Button>,
        ]}
      >
        <Alert
          message="Chú ý"
          description="Chọn một đơn hàng chính. Tất cả các đơn hàng khác sẽ được gộp vào đơn hàng này."
          type="warning"
          className="mb-3"
          showIcon
        />

        <Form form={combineForm} layout="vertical">
          <Form.Item
            label="Chọn đơn hàng chính"
            name="main_order_code"
            rules={[{ required: true, message: 'Vui lòng chọn đơn hàng chính!' }]}
          >
            <Select
              placeholder="Chọn đơn hàng chính"
              options={selected.map((order) => ({
                value: order.order_code,
                label: order.order_code,
              }))}
            />
          </Form.Item>
        </Form>

        <div className="mb-3">
          <strong>Các đơn hàng sẽ gộp:</strong>
          <ul className="mt-2">
            {selected.map((order) => (
              <li key={order.order_id}>{order.order_code}</li>
            ))}
          </ul>
        </div>
      </Modal>
    </div>
  );
};

export default DebitCustomer;
